import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Leaf } from 'lucide-react';

/**
 * Interface for cow dung transaction data point
 */
export interface CowDungTransactionChartData {
  date: string;
  weightKg: number;
  totalAmount: number;
  qualityGrade?: string;
  transactionCount?: number;
}

interface CowDungTransactionChartProps {
  data: CowDungTransactionChartData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Custom tooltip component for cow dung transaction display
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const weightDataPoint = payload.find(p => p.name === 'Weight (KG)');
    const amountDataPoint = payload.find(p => p.name === 'Value (₹)');

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700">{label}</p>
        {weightDataPoint && (
          <p className="text-green-600">
            Weight: <span className="font-bold">{weightDataPoint.value} KG</span>
          </p>
        )}
        {amountDataPoint && (
          <p className="text-blue-600">
            Value: <span className="font-bold">₹{amountDataPoint.value}</span>
          </p>
        )}
      </div>
    );
  }
  return null;
};

/**
 * Cow Dung Transaction Chart Component
 * Displays daily cow dung collection weight and transaction value
 */
export const CowDungTransactionChart: React.FC<CowDungTransactionChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 300,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: `${height}px` }}>
        <div className="text-center">
          <p className="text-gray-600">Loading transaction data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height: `${height}px` }}>
        <div className="text-center">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height: `${height}px` }}>
        <div className="text-center">
          <Leaf className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No cow dung transactions found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            stroke="#999"
            style={{ fontSize: '12px' }}
            tick={{ fill: '#666' }}
          />
          <YAxis
            stroke="#999"
            style={{ fontSize: '12px' }}
            tick={{ fill: '#666' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="square"
          />
          <Bar
            dataKey="weightKg"
            fill="#10b981"
            name="Weight (KG)"
            radius={[8, 8, 0, 0]}
            animationDuration={500}
          />
          <Bar
            dataKey="totalAmount"
            fill="#3b82f6"
            name="Value (₹)"
            radius={[8, 8, 0, 0]}
            animationDuration={500}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CowDungTransactionChart;
